import { permissionsSelectors } from 'ducks/permissions'
import { useEffect, useMemo } from 'react'
import { useDataProvider, useNotify, useRefresh, useTranslate, useUnselectAll } from 'react-admin'
import { useSelector } from 'react-redux'
import { RootState } from 'types/state'

import { BulkEditDialogForm } from './BulkEditDialogForm'
import {
  BulkEditAutoAcceptErrorState,
  BulkEditAutoAcceptPendingState,
  BulkEditAutoAcceptWarningState,
  BulkEditBulkConfirmPanel,
  BulkEditEmptySelectionState,
  BulkEditNoPermissionState,
  BulkEditResolveFailedState,
  BulkEditResolvingState,
} from './BulkEditDialogStates'
import { mergeBulkEditInitialValues } from './helpers'
import { useBulkEditAutoAccept } from './hooks/useBulkEditAutoAccept'
import { useBulkEditResolvedIds } from './hooks/useBulkEditResolvedIds'
import type { BulkEditAutoAcceptPolicy, BulkEditDialogOpenPayload, BulkEditResourceDefinition } from './types'

type Props = {
  definition: BulkEditResourceDefinition
  session: BulkEditDialogOpenPayload
  onClose: () => void
  onAutoApplyBlockingChange?: (blocking: boolean) => void
}

export const BulkEditDialogBody = ({ definition: def, session, onClose, onAutoApplyBlockingChange }: Props) => {
  const translate = useTranslate()
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const unselectAll = useUnselectAll()
  const access = useSelector((state: RootState) => permissionsSelectors.getPermissionByKey(state, def.permissionKey))

  const { effectiveIds, resolving, resolveError } = useBulkEditResolvedIds({
    resource: session.resource,
    selectedIds: session.selectedIds,
    listQuery: session.listQuery,
    dataProvider,
    translate,
  })

  const fieldVisibility = session.visibleFieldKeys ?? def.defaultVisibleFieldKeys
  const formFieldVisibility: string[] | null = fieldVisibility === undefined ? null : fieldVisibility
  const patchVisibilityKeys = fieldVisibility === undefined ? undefined : fieldVisibility

  const autoAcceptPolicy = useMemo((): BulkEditAutoAcceptPolicy => {
    return session.autoAcceptMode ?? false
  }, [session.autoAcceptMode])

  const { patchIsEmpty, saveFailed, awaitingModalConfirm, acknowledgeBulkConfirm, isApplying } = useBulkEditAutoAccept({
    autoAcceptPolicy,
    resource: session.resource,
    initialValues: session.initialValues,
    definition: def,
    effectiveIds,
    resolving,
    patchVisibilityKeys,
    dataProvider,
    notify,
    refresh,
    unselectAll,
    translate,
    onClose,
  })

  useEffect(() => {
    if (autoAcceptPolicy === false) {
      onAutoApplyBlockingChange?.(false)
      return
    }
    onAutoApplyBlockingChange?.(isApplying)
  }, [autoAcceptPolicy, isApplying, onAutoApplyBlockingChange])

  const initialFormValues = useMemo(
    () => mergeBulkEditInitialValues(def, session.initialValues),
    [def, session.initialValues]
  )

  if (!access.allowEdit) {
    return <BulkEditNoPermissionState translate={translate} />
  }

  if (resolving) {
    return <BulkEditResolvingState translate={translate} />
  }

  if (resolveError) {
    return <BulkEditResolveFailedState message={resolveError} />
  }

  if (effectiveIds.length === 0) {
    return <BulkEditEmptySelectionState translate={translate} />
  }

  if (autoAcceptPolicy !== false) {
    if (patchIsEmpty) {
      return (
        <BulkEditAutoAcceptWarningState
          message={translate('Auto-accept needs at least one field selected. Nothing was applied.')}
        />
      )
    }
    if (saveFailed) {
      return (
        <BulkEditAutoAcceptErrorState
          translate={translate}
          effectiveCount={effectiveIds.length}
          effectiveIds={effectiveIds}
        />
      )
    }
    if (awaitingModalConfirm) {
      return (
        <BulkEditBulkConfirmPanel
          message={translate('Apply these changes to all selected records?')}
          selectedCountText={translate('Selected %{smart_count} record |||| Selected %{smart_count} records', {
            smart_count: effectiveIds.length,
          })}
          onBack={onClose}
          onApply={acknowledgeBulkConfirm}
          applying={false}
          translate={translate}
          backButtonKey="ra.action.cancel"
        />
      )
    }
    return <BulkEditAutoAcceptPendingState translate={translate} effectiveCount={effectiveIds.length} />
  }

  return (
    <BulkEditDialogForm
      definition={def}
      resource={session.resource}
      effectiveIds={effectiveIds}
      initialFormValues={initialFormValues}
      formFieldVisibility={formFieldVisibility}
      patchVisibilityKeys={patchVisibilityKeys}
      onClose={onClose}
    />
  )
}
